import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Loader2, User, Shield, Mail, Users, Lock, Eye, EyeOff, Link, CheckCircle, Calendar, PenLine, Info, Bold, Italic, HardDrive, Compass, PhoneCall, AlertCircle, AlertTriangle, RefreshCw, Globe, Camera, Trash2, Bell, Sparkles, Accessibility, Target, Keyboard, CreditCard, Coins, TrendingUp, ChevronDown, ChevronUp, Plus, Minus, Clock } from "lucide-react";
import { CreditDistributionCard, UserCreditBalanceCard } from "@/components/admin/credit-distribution-card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { UserRole, UserProfile, BrandingSettings } from "@shared/schema";
import NotificationSettingsPage from "@/pages/notification-settings";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useProfilePictureUpload } from "@/hooks/use-profile-picture-upload";
import { SquareCropDialog } from "@/components/square-crop-dialog";
import { getAvatarSource, getAvatarInitials } from "@/lib/avatar";
import { DomainStatusPanel } from "@/components/domain-status-panel";
import { AccessRequestsDialog } from "@/components/access-requests-dialog";
import { EventOfferingsDialog } from "@/components/event-offerings-dialog";
import { LateFeeSettingsDialog } from "@/components/late-fee-settings-dialog";
// Re-export so any external importer that already grabs LEAD_COLUMN_OPTIONS
// from this module keeps working. Source of truth is the leaf module
// (no React deps) so admin/* surfaces don't drag this 2000-line page
// into their chunk and trip a TDZ in the minified production bundle.
export { LEAD_COLUMN_OPTIONS } from "@/lib/lead-column-options";
import { LEAD_COLUMN_OPTIONS } from "@/lib/lead-column-options";

interface SettingsPageProps {
  effectiveRole?: UserRole;
  effectiveUserId?: string;
  isViewingAsUser?: boolean;
}

type SettingsCategoryKey =
  | "profile"
  | "connections"
  | "email"
  | "dialer"
  | "goals"
  | "leads"
  | "aiTools"
  | "accessibility"
  | "notifications"
  | "calendarSharing"
  | "timecard";

const SETTINGS_CATEGORIES: Array<{
  key: SettingsCategoryKey;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}> = [
  { key: "profile", label: "Profile", icon: User, description: "Name, photo, password, time zone" },
  { key: "connections", label: "Connections", icon: Link, description: "Gmail, Calendar" },
  { key: "email", label: "Email", icon: Mail, description: "Your outgoing email signature" },
  { key: "calendarSharing", label: "Calendar", icon: Calendar, description: "Office hours, sharing mode, overlay defaults, ICS feed" },
  { key: "leads", label: "Leads", icon: Users, description: "Lead list, client type, defaults" },
  { key: "goals", label: "Goals", icon: Target, description: "Daily Drive widget & themes" },
  { key: "dialer", label: "Dialer", icon: PhoneCall, description: "Browser calling preferences" },
  { key: "aiTools", label: "AI Tools", icon: Sparkles, description: "Coach, Catch Me Up, AI helpers" },
  { key: "notifications", label: "Notifications", icon: Bell, description: "Channels, digests" },
  { key: "accessibility", label: "Accessibility", icon: Accessibility, description: "Motion, text size, contrast, voice" },
  { key: "timecard", label: "Time Card", icon: Clock, description: "Clock in/out, shift history" },
];

interface WorkingHours {
  enabled: boolean;
  start: string;
  end: string;
  timezone: string;
  days: number[];
}

const TIMEZONES = [
  "America/Chicago",
  "America/New_York",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
];

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function SettingsPage({ effectiveRole, effectiveUserId, isViewingAsUser }: SettingsPageProps) {
  const { user, refetchUser } = useAuth();
  const { toast } = useToast();
  const queryClientHook = useQueryClient();
  const [, navigate] = useLocation();
  
  // Sidebar category state — honour ?tab=<key> deep-link from other pages
  const initialCategory = (): SettingsCategoryKey => {
    const t = new URLSearchParams(window.location.search).get("tab");
    const valid: SettingsCategoryKey[] = ["profile", "connections", "email", "dialer", "goals", "leads", "aiTools", "accessibility", "notifications", "calendarSharing", "timecard"];
    return (valid.includes(t as SettingsCategoryKey) ? t : "profile") as SettingsCategoryKey;
  };
  const [selectedCategory, setSelectedCategory] = useState<SettingsCategoryKey>(initialCategory);

  // Finalize a user-level credit pack purchase after the Stripe redirect back.
  // The buy flow (UserCreditBalanceCard) sends users to
  // /settings?section=<aiTools|leads>&credit_purchase=success&session_id=...
  // but nothing previously consumed that — so the user paid and never got
  // credits. This calls the matching confirm endpoint (server-side idempotent).
  const creditConfirmRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const status = params.get("credit_purchase");
    if (status !== "success" && status !== "cancelled") return;
    if (creditConfirmRef.current) return;
    creditConfirmRef.current = true;
    const section = params.get("section");
    const sessionId = params.get("session_id");
    const cleanUrl = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("credit_purchase");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.pathname + url.search);
    };
    if (status === "cancelled") {
      toast({ title: "Purchase cancelled", description: "No credits were added." });
      cleanUrl();
      return;
    }
    if (!sessionId) {
      cleanUrl();
      return;
    }
    const endpoint = section === "leads"
      ? "/api/user-credits/lead/checkout/confirm"
      : "/api/user-credits/ai/checkout/confirm";
    (async () => {
      try {
        const res = await apiRequest("POST", endpoint, { sessionId });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error((e as any)?.message || "Could not confirm purchase");
        }
        queryClient.invalidateQueries({ queryKey: ["/api/me/credit-balance"] });
        toast({ title: "Credits added", description: "Your balance has been updated." });
        // Only strip the params once credited. On failure we keep session_id so a
        // refresh retries the (idempotent) confirm rather than stranding a
        // paid-but-uncredited session.
        cleanUrl();
      } catch (err: any) {
        toast({
          title: "Purchase not confirmed",
          description: err?.message || "If credits are missing, contact your admin.",
          variant: "destructive",
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // User profile state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [personalPhone, setPersonalPhone] = useState("");
  
  const [accessRequestsOpen, setAccessRequestsOpen] = useState(false);
  const [eventOfferingsOpen, setEventOfferingsOpen] = useState(false);
  const [lateFeeSettingsOpen, setLateFeeSettingsOpen] = useState(false);
  // Consolidated account dialog state
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);

  // Password management state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const { data: profile, isLoading } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
    enabled: !!user,
  });

  const { data: meProfile } = useQuery<{ profile: { profilePicture: string | null } | null }>({
    queryKey: ["/api/profiles/me"],
    enabled: !!user,
  });
  const settingsPhotoUrl = getAvatarSource({
    profilePicture: meProfile?.profile?.profilePicture,
    profileImageUrl: user?.profileImageUrl,
  });
  const settingsHasCustomPhoto = !!meProfile?.profile?.profilePicture;
  const {
    pickFile: pickSettingsPhoto,
    removePhoto: removeSettingsPhoto,
    fileInputProps: settingsPhotoInputProps,
    selectedFile: settingsSelectedFile,
    cropOpen: settingsCropOpen,
    cancelCrop: cancelSettingsCrop,
    confirmCrop: confirmSettingsCrop,
    isUploading: settingsPhotoUploading,
    isRemoving: settingsPhotoRemoving,
  } = useProfilePictureUpload();

  const { data: viewingAsUserData } = useQuery<{ id: string; firstName: string; lastName: string; email: string }>({
    queryKey: ["/api/workspace/user", effectiveUserId],
    queryFn: async () => {
      const res = await fetch(`/api/workspace/user/${effectiveUserId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch user");
      return res.json();
    },
    enabled: !!isViewingAsUser && !!effectiveUserId,
  });

  const { data: passwordStatus } = useQuery<{ hasPassword: boolean }>({
    queryKey: ["/api/user/has-password"],
    enabled: !!user,
  });

  const { data: workingHoursPrefs } = useQuery<{ workingHours: WorkingHours }>({
    queryKey: ["/api/notification-preferences"],
    enabled: !!user,
  });

  const [working, setWorking] = useState<WorkingHours | null>(null);

  useEffect(() => {
    if (workingHoursPrefs?.workingHours && !working) {
      setWorking(workingHoursPrefs.workingHours);
    }
  }, [workingHoursPrefs]);

  const saveWorkingHours = useMutation({
    mutationFn: () =>
      apiRequest("PUT", "/api/notification-preferences", {
        preferences: {},
        workingHours: working || undefined,
      }),
    onSuccess: () => {
      toast({ title: "Working hours saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/notification-preferences"] });
    },
    onError: (e: any) =>
      toast({ title: "Failed to save", description: e?.message, variant: "destructive" }),
  });

  // Gmail connection status with redundancy info for admins
  const { data: gmailStatus, isLoading: gmailLoading } = useQuery<{
    connected: boolean;
    email: string | null;
    valid: boolean;
    hasRedundancy: boolean;
    userTokensConnected: boolean;
    userTokensAuthorized: boolean;
    userTokensEmail: string | null;
    connectorConnected: boolean;
    connectorEmail: string | null;
    inboundReadingAvailable: boolean;
    gmailReadEnabled: boolean;
    userHasReadScope: boolean;
    readAccessState: "none" | "requested" | "rejected" | "granted_pending_connect" | "connected";
    readGrantStatus: string | null;
    readRequestStatus: string | null;
    readRequestDecisionNote: string | null;
  }>({
    queryKey: ["/api/gmail/status"],
    enabled: !!user,
  });

  // User Calendar connection status (personal calendar for this user)
  const { data: userCalendarStatus, isLoading: userCalendarLoading } = useQuery<{ connected: boolean; email: string | null }>({
    queryKey: ["/api/user/calendar/status"],
    enabled: !!user,
  });

  // Email Signature state
  const [emailSignature, setEmailSignature] = useState("");
  const [useLogoInSignature, setUseLogoInSignature] = useState(true);
  const signatureTextareaRef = useRef<HTMLTextAreaElement>(null);

  const applyFormatting = (tag: 'b' | 'i') => {
    const textarea = signatureTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = emailSignature.substring(start, end);
    
    if (selectedText) {
      const before = emailSignature.substring(0, start);
      const after = emailSignature.substring(end);
      const formatted = `<${tag}>${selectedText}</${tag}>`;
      setEmailSignature(before + formatted + after);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start, start + formatted.length);
      }, 0);
    } else {
      const before = emailSignature.substring(0, start);
      const after = emailSignature.substring(start);
      const placeholder = `<${tag}></${tag}>`;
      setEmailSignature(before + placeholder + after);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + tag.length + 2, start + tag.length + 2);
      }, 0);
    }
  };

  // Branding settings to check if logo is forced
  const { data: brandingSettings } = useQuery<BrandingSettings>({
    queryKey: ["/api/branding/settings"],
    enabled: !!user,
  });

  // Initialize signature fields when profile loads
  useEffect(() => {
    if (profile) {
      setEmailSignature(profile.emailSignature || "");
      setUseLogoInSignature(profile.useLogoInSignature ?? true);
    }
  }, [profile]);

  const isLogoForced = brandingSettings?.forceLogoInSignature ?? false;
  const logoUrl = brandingSettings?.logoPath || null;

  const updateSignatureMutation = useMutation({
    mutationFn: async (data: { emailSignature: string; useLogoInSignature: boolean }) => {
      const response = await apiRequest("PATCH", "/api/profile/signature", data);
      return response.json();
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/profile"] });
      toast({ title: "Signature Saved", description: "Your email signature has been updated." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update signature.", variant: "destructive" });
    },
  });

  const handleSaveSignature = () => {
    updateSignatureMutation.mutate({
      emailSignature,
      useLogoInSignature: isLogoForced ? true : useLogoInSignature,
    });
  };

  const connectGmailMutation = useMutation({
    mutationFn: async () => {
      // Pass the current URL to preserve workspace context after OAuth redirect.
      // Use raw fetch (GET needs no CSRF token) so we can inspect the HTTP
      // status and response body to distinguish auth failures from real errors.
      const currentUrl = window.location.href;
      const res = await fetch(
        `/api/gmail/auth?returnUrl=${encodeURIComponent(currentUrl)}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.message || "Failed to initiate Gmail connection");
        (err as any).status = res.status;
        (err as any).code = body.code;
        throw err;
      }
      const data = await res.json();
      return data.authUrl;
    },
    onSuccess: (authUrl: string) => {
      window.location.href = authUrl;
    },
    onError: (error: any) => {
      // Session expired or workspace unresolvable — route to re-login.
      if (
        error?.status === 401 ||
        (error?.status === 400 && error?.code === "WORKSPACE_REQUIRED")
      ) {
        const returnTo = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.href = `/api/login?silent=1&returnTo=${returnTo}`;
        return;
      }
      toast({
        title: "Error",
        description: error.message || "Failed to initiate Gmail connection",
        variant: "destructive",
      });
    },
  });

  const disconnectGmailMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/gmail/disconnect");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gmail/status"] });
      toast({
        title: "Gmail Disconnected",
        description: "Gmail has been disconnected. You can reconnect it anytime.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to disconnect Gmail",
        variant: "destructive",
      });
    },
  });

  // Task #244 — request inbox read access (super-admin approval required).
  const requestReadAccessMutation = useMutation({
    mutationFn: async (reason: string) => {
      const response = await apiRequest("POST", "/api/gmail/read/request", { reason });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gmail/status"] });
      toast({ title: "Request submitted", description: "An admin will review your inbox read access request." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to submit request", variant: "destructive" });
    },
  });

  // Task #409 — "Notify me when it's live" demand signal for inbox reading.
  // Passive log only (no email sent now); the confirmed state persists across
  // reloads via the status query.
  const { data: readNotifyStatus } = useQuery<{ notified: boolean; interest: any }>({
    queryKey: ["/api/gmail/read/notify/status"],
  });
  const notifyReadAccessMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/gmail/read/notify", {});
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gmail/read/notify/status"] });
      toast({ title: "You're on the list", description: "We'll let you know when inbox reading is available." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to record your interest", variant: "destructive" });
    },
  });

  // Task #388 — Outlook support request (demand signal). Does NOT connect or
  // send any Outlook mail; it just logs that the user wants Outlook support.
  const { data: outlookRequestStatus } = useQuery<{ requested: boolean; request: any }>({
    queryKey: ["/api/outlook/request/status"],
  });
  const [outlookReason, setOutlookReason] = useState("");
  const requestOutlookSupportMutation = useMutation({
    mutationFn: async (reason: string) => {
      const response = await apiRequest("POST", "/api/outlook/request", { reason });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/outlook/request/status"] });
      setOutlookReason("");
      toast({ title: "Request logged", description: "Thanks — we'll use this to gauge demand for Outlook support." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to submit request", variant: "destructive" });
    },
  });

  // Task #244 — appeal a rejected request.
  const appealReadAccessMutation = useMutation({
    mutationFn: async (appealText: string) => {
      const response = await apiRequest("POST", "/api/gmail/read/appeal", { appealText });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/gmail/status"] });
      toast({ title: "Appeal submitted", description: "Your appeal will be reviewed again." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to submit appeal", variant: "destructive" });
    },
  });

  // Task #244 — once granted, complete the gated read OAuth flow (adds the
  // restricted gmail.readonly scope). This is the irreversible seat-consuming
  // step.
  const connectReadMutation = useMutation({
    mutationFn: async () => {
      const currentUrl = window.location.href;
      const response = await apiRequest("GET", `/api/gmail/read/auth?returnUrl=${encodeURIComponent(currentUrl)}`);
      const data = await response.json();
      return data.authUrl as string;
    },
    onSuccess: (authUrl: string) => {
      window.location.href = authUrl;
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to start inbox read connection", variant: "destructive" });
    },
  });

  // User Calendar mutations (personal calendar)
  const connectUserCalendarMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", "/api/user/calendar/auth");
      const data = await response.json();
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
      return data;
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to connect calendar",
        variant: "destructive",
      });
    },
  });

  const disconnectUserCalendarMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/user/calendar/disconnect");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/calendar/status"] });
      toast({
        title: "Personal Calendar Disconnected",
        description: "Your personal calendar has been disconnected.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to disconnect calendar",
        variant: "destructive",
      });
    },
  });

  // Google Drive status and mutations
  const { data: driveStatus, isLoading: driveLoading } = useQuery<{ connected: boolean; email: string | null }>({
    queryKey: ["/api/drive/status"],
  });

  const connectDriveMutation = useMutation({
    mutationFn: async () => {
      const currentUrl = window.location.href;
      const response = await apiRequest("GET", `/api/drive/auth?returnUrl=${encodeURIComponent(currentUrl)}`);
      const data = await response.json();
      return data.authUrl;
    },
    onSuccess: (authUrl: string) => {
      window.location.href = authUrl;
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to initiate Google Drive connection",
        variant: "destructive",
      });
    },
  });

  const disconnectDriveMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/drive/disconnect");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/drive/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/integrations/status"] });
      toast({ title: "Google Drive Disconnected", description: "Google Drive has been disconnected." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to disconnect Drive", variant: "destructive" });
    },
  });

  // Handle OAuth connection callbacks (Gmail, Calendar, Drive)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmailParam = params.get("gmail");
    const userCalendarParam = params.get("user_calendar");
    const driveParam = params.get("drive");
    const reason = params.get("reason");

    if (gmailParam === "connected") {
      toast({
        title: "Gmail Connected",
        description: "Your Gmail account has been connected successfully!",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/gmail/status"] });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (gmailParam === "error") {
      toast({
        title: "Gmail Connection Failed",
        description: reason || "Failed to connect Gmail account",
        variant: "destructive",
      });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (gmailParam === "read_connected") {
      toast({
        title: "Inbox reading enabled",
        description: "Reply detection and inbox features are now active for your account.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/gmail/status"] });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (gmailParam === "read_declined") {
      toast({
        title: "Inbox reading not enabled",
        description: "Read permission wasn't granted. You can try again anytime.",
        variant: "destructive",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/gmail/status"] });
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (userCalendarParam === "success") {
      const email = params.get("email");
      toast({
        title: "Personal Calendar Connected",
        description: `Your calendar${email ? ` (${email})` : ''} has been connected successfully!`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/calendar/status"] });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (userCalendarParam === "error") {
      toast({
        title: "Calendar Connection Failed",
        description: reason || "Failed to connect calendar",
        variant: "destructive",
      });
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (driveParam === "connected") {
      toast({
        title: "Google Drive Connected",
        description: "Google Drive has been connected successfully!",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/drive/status"] });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (driveParam === "error") {
      toast({
        title: "Drive Connection Failed",
        description: reason || "Failed to connect Google Drive",
        variant: "destructive",
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [toast]);

  // Populate user profile fields when user data is loaded
  // When impersonating, use the impersonated user's data
  useEffect(() => {
    if (isViewingAsUser && viewingAsUserData) {
      setFirstName(viewingAsUserData.firstName || "");
      setLastName(viewingAsUserData.lastName || "");
      setEmail(viewingAsUserData.email || "");
    } else if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEmail(user.email || "");
    }
  }, [user, isViewingAsUser, viewingAsUserData]);

  const { data: personalPhoneData } = useQuery<{ bridgePhone: string | null }>({
    queryKey: ["/api/settings/dialer"],
    select: (d: any) => ({ bridgePhone: d?.bridgePhone ?? null }),
    enabled: !!user,
  });
  useEffect(() => {
    setPersonalPhone(personalPhoneData?.bridgePhone || "");
  }, [personalPhoneData]);

  const updateUserMutation = useMutation({
    mutationFn: async (data: { firstName: string; lastName: string; email: string }) => {
      const phoneVal = personalPhone.trim();
      if (phoneVal) {
        await apiRequest("PATCH", "/api/settings/dialer", { bridgePhone: phoneVal || null });
      } else {
        await apiRequest("PATCH", "/api/settings/dialer", { bridgePhone: null });
      }
      return apiRequest("PATCH", "/api/user", data);
    },
    onSuccess: () => {
      refetchUser();
      queryClient.invalidateQueries({ queryKey: ["/api/settings/dialer"] });
      toast({ title: "Profile saved", description: "Your profile has been updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update profile.", variant: "destructive" });
    },
  });

  // Password mutations
  const setPasswordMutation = useMutation({
    mutationFn: async (data: { password: string; confirmPassword: string }) => {
      return apiRequest("POST", "/api/user/password", data);
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/user/has-password"] });
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordModal(false);
      toast({ title: "Password set", description: "Your password has been set successfully. You can now log in with email and password." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to set password.", variant: "destructive" });
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
      return apiRequest("PATCH", "/api/user/password", data);
    },
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPasswordModal(false);
      toast({ title: "Password changed", description: "Your password has been updated successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to change password.", variant: "destructive" });
    },
  });

  const handleSaveProfile = () => {
    updateUserMutation.mutate({ firstName, lastName, email });
  };

  const handleSetPassword = () => {
    if (newPassword.length < 8) {
      toast({ title: "Error", description: "Password must be at least 8 characters.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "Passwords do not match.", variant: "destructive" });
      return;
    }
    setPasswordMutation.mutate({ password: newPassword, confirmPassword });
  };

  const handleChangePassword = () => {
    if (newPassword.length < 8) {
      toast({ title: "Error", description: "New password must be at least 8 characters.", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "Passwords do not match.", variant: "destructive" });
      return;
    }
    changePasswordMutation.mutate({ currentPassword, newPassword, confirmPassword });
  };

  if (isLoading && !profile) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const activeCategoryMeta = SETTINGS_CATEGORIES.find((c) => c.key === selectedCategory) || SETTINGS_CATEGORIES[0];

  return (
    <div className="flex h-full min-h-0">
      <aside
        className="w-60 shrink-0 border-r border-border bg-card/40 overflow-y-auto hidden md:block"
        data-testid="settings-sidebar"
      >
        <div className="p-4 border-b border-border">
          <h1 className="text-lg font-semibold">Settings</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Your preferences</p>
        </div>
        <nav className="p-2 space-y-0.5">
          {SETTINGS_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const active = selectedCategory === cat.key;
            return (
              <button
                key={cat.key}
                type="button"
                onClick={() => setSelectedCategory(cat.key)}
                className={cn(
                  "w-full flex items-start gap-3 px-3 py-2 rounded-md text-left transition-colors",
                  active
                    ? "bg-primary/10 text-primary"
                    : "hover-elevate active-elevate-2 text-foreground"
                )}
                data-testid={`button-settings-category-${cat.key}`}
              >
                <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium">{cat.label}</div>
                  <div className="text-xs text-muted-foreground truncate">{cat.description}</div>
                </div>
              </button>
            );
          })}
        </nav>
      </aside>
      <main className="flex-1 overflow-y-auto min-w-0">
        <div className="md:hidden px-3 pt-3">
          <Select value={selectedCategory} onValueChange={(v) => setSelectedCategory(v as SettingsCategoryKey)}>
            <SelectTrigger data-testid="select-settings-category-mobile">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SETTINGS_CATEGORIES.map((cat) => (
                <SelectItem key={cat.key} value={cat.key}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="p-3 sm:p-6 max-w-4xl mx-auto space-y-4 sm:space-y-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold" data-testid="text-settings-category-title">{activeCategoryMeta.label}</h1>
            <p className="text-sm text-muted-foreground">{activeCategoryMeta.description}</p>
          </div>

      {selectedCategory === "profile" && (
      <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="relative group cursor-pointer" onClick={pickSettingsPhoto} data-testid="button-header-change-photo">
              <input {...settingsPhotoInputProps} data-testid="input-settings-photo" />
              <Avatar className="h-10 w-10">
                <AvatarImage src={settingsPhotoUrl} />
                <AvatarFallback>
                  {getAvatarInitials(
                    user?.firstName && user?.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.firstName || user?.email || "User",
                  )}
                </AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                {settingsPhotoUploading ? (
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                ) : (
                  <Camera className="w-3 h-3 text-white" />
                )}
              </div>
            </div>
            <div>
              <CardTitle className="text-lg">Account</CardTitle>
              <CardDescription>
                Your profile photo, name, email, and login password.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* ── Name & Email ──────────────────────────────────────── */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter first name"
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter last name"
                  data-testid="input-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="personal-phone">Personal phone</Label>
              <Input
                id="personal-phone"
                value={personalPhone}
                onChange={(e) => setPersonalPhone(e.target.value)}
                placeholder="+15551234567"
                data-testid="input-personal-phone"
              />
              <p className="text-xs text-muted-foreground">
                Your mobile or desk phone. When set, inbound calls routed to you will ring this phone alongside your browser tab ("Dial to my phone").
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleSaveProfile}
                disabled={updateUserMutation.isPending}
                data-testid="button-save-profile"
              >
                {updateUserMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Profile
              </Button>
            </div>
          </div>

          <div className="border-t" />

          {/* ── Password ──────────────────────────────────────────── */}
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium">Password</p>
            </div>
            {isViewingAsUser ? (
              <div
                className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-200"
                data-testid="banner-password-impersonation-blocked"
              >
                Password changes are disabled while you're logged in as another user.
                Use Admin → Reset Password from your own session to reset this user's password.
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowPasswordModal(true)}
                data-testid="button-reset-password"
              >
                <Lock className="w-4 h-4 mr-2" />
                Reset Password
              </Button>
            )}
          </div>

          <Dialog
            open={showPasswordModal}
            onOpenChange={(open) => {
              if (!open) {
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
                setShowCurrentPassword(false);
                setShowNewPassword(false);
                setShowConfirmPassword(false);
              }
              setShowPasswordModal(open);
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{passwordStatus?.hasPassword ? "Change Password" : "Set Password"}</DialogTitle>
                <DialogDescription>
                  {passwordStatus?.hasPassword
                    ? "Enter your current password and choose a new one."
                    : "Setting a password allows you to log in using your email address instead of Replit authentication."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                {passwordStatus?.hasPassword ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">Current Password</Label>
                      <div className="relative">
                        <Input
                          id="currentPassword"
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="Enter current password"
                          data-testid="input-current-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newPasswordChange">New Password</Label>
                      <div className="relative">
                        <Input
                          id="newPasswordChange"
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password (min 8 characters)"
                          data-testid="input-new-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPasswordChange">Confirm New Password</Label>
                      <div className="relative">
                        <Input
                          id="confirmPasswordChange"
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm new password"
                          data-testid="input-confirm-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New Password</Label>
                      <div className="relative">
                        <Input
                          id="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password (min 8 characters)"
                          data-testid="input-new-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm new password"
                          data-testid="input-confirm-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                {passwordStatus?.hasPassword ? (
                  <Button
                    onClick={handleChangePassword}
                    disabled={changePasswordMutation.isPending || !currentPassword || !newPassword || !confirmPassword}
                    data-testid="button-change-password"
                  >
                    {changePasswordMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Change Password
                  </Button>
                ) : (
                  <Button
                    onClick={handleSetPassword}
                    disabled={setPasswordMutation.isPending || !newPassword || !confirmPassword}
                    data-testid="button-set-password"
                  >
                    {setPasswordMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Set Password
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      </>
      )}

      <SquareCropDialog
        open={settingsCropOpen}
        file={settingsSelectedFile}
        onCancel={cancelSettingsCrop}
        onConfirm={confirmSettingsCrop}
        isUploading={settingsPhotoUploading}
      />

      {selectedCategory === "connections" && (
      /* Connect to GSuite Card */
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Link className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Connect to GSuite</CardTitle>
              <CardDescription>
                Connect Gmail and Google Calendar for email and booking sync
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 pt-0">
          {/* Gmail Row */}
          <div className="flex items-center justify-between py-2 border-b border-border/50">
            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <div>
                <span className="font-medium text-sm">Gmail</span>
                {gmailStatus?.userTokensConnected && gmailStatus?.userTokensEmail && gmailStatus?.valid !== false ? (
                  <p className="text-xs text-muted-foreground">{gmailStatus.userTokensEmail}</p>
                ) : gmailStatus?.userTokensConnected && gmailStatus?.userTokensEmail && gmailStatus?.valid === false ? (
                  <p className="text-xs text-amber-600">{gmailStatus.userTokensEmail} — token expired, reconnect recommended</p>
                ) : gmailStatus?.userTokensAuthorized && gmailStatus?.userTokensEmail ? (
                  <p className="text-xs text-amber-600">{gmailStatus.userTokensEmail} — reconnect required</p>
                ) : gmailStatus?.connected && !gmailStatus?.userTokensConnected ? (
                  <p className="text-xs text-amber-600">Using workspace fallback - connect your Gmail to send from your email</p>
                ) : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {gmailLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              ) : gmailStatus?.userTokensConnected && gmailStatus?.valid === false ? (
                /* Token row exists and was connected, but the refresh token is stale */
                <>
                  <span className="flex items-center gap-1 text-xs text-amber-600">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Reconnect recommended
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => connectGmailMutation.mutate()}
                    disabled={connectGmailMutation.isPending || isViewingAsUser}
                    title={isViewingAsUser ? "Disabled while logged in as another user" : undefined}
                    data-testid="button-reconnect-gmail-stale"
                  >
                    {connectGmailMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    Reconnect
                  </Button>
                </>
              ) : gmailStatus?.userTokensConnected ? (
                <>
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="w-3.5 h-3.5" />
                    {/* Show redundancy status only for admin users */}
                    {(effectiveRole === 'admin' || profile?.role === 'admin') && gmailStatus?.hasRedundancy 
                      ? "Connected (with redundancy)" 
                      : "Connected"}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => disconnectGmailMutation.mutate()}
                    disabled={disconnectGmailMutation.isPending || isViewingAsUser}
                    title={isViewingAsUser ? "Disabled while logged in as another user" : undefined}
                    data-testid="button-disconnect-gmail"
                  >
                    {disconnectGmailMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Disconnect"}
                  </Button>
                </>
              ) : gmailStatus?.userTokensAuthorized ? (
                /* Stale / expired token — show Reconnect (not Connect) so the user
                   knows they've been here before and just need to re-authorise. */
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => connectGmailMutation.mutate()}
                  disabled={connectGmailMutation.isPending || isViewingAsUser}
                  title={isViewingAsUser ? "Disabled while logged in as another user" : undefined}
                  data-testid="button-reconnect-gmail"
                >
                  {connectGmailMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                  Reconnect
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => connectGmailMutation.mutate()}
                  disabled={connectGmailMutation.isPending || isViewingAsUser}
                  title={isViewingAsUser ? "Disabled while logged in as another user" : undefined}
                  data-testid="button-connect-gmail"
                >
                  {connectGmailMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                  Connect
                </Button>
              )}
            </div>
          </div>

          {/* Personal Calendar Row */}
          <div className="flex items-center justify-between py-2 border-b border-border/50">
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <div>
                <span className="font-medium text-sm">My Calendar</span>
                {userCalendarStatus?.connected ? (
                  <p className="text-xs text-muted-foreground">{userCalendarStatus.email}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Your personal calendar for availability</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {userCalendarLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              ) : userCalendarStatus?.connected ? (
                <>
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Connected
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => disconnectUserCalendarMutation.mutate()}
                    disabled={disconnectUserCalendarMutation.isPending || isViewingAsUser}
                    title={isViewingAsUser ? "Disabled while logged in as another user" : undefined}
                    data-testid="button-disconnect-user-calendar"
                  >
                    {disconnectUserCalendarMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Disconnect"}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  className="h-7"
                  onClick={() => connectUserCalendarMutation.mutate()}
                  disabled={connectUserCalendarMutation.isPending || isViewingAsUser}
                  title={isViewingAsUser ? "Disabled while logged in as another user" : undefined}
                  data-testid="button-connect-user-calendar"
                >
                  {connectUserCalendarMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                  Connect
                </Button>
              )}
            </div>
          </div>

          {/* Google Drive Row — hidden from UI; all OAuth/query/mutation logic remains intact. To re-enable, un-comment this block. */}
          {false && (
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <HardDrive className="w-4 h-4 text-muted-foreground" />
              <div>
                <span className="font-medium text-sm">Google Drive</span>
                {driveStatus?.connected && driveStatus.email ? (
                  <p className="text-xs text-muted-foreground">{driveStatus.email}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Connect for Google Sheets sync</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {driveLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              ) : driveStatus?.connected ? (
                <>
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Connected
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => disconnectDriveMutation.mutate()}
                    disabled={disconnectDriveMutation.isPending}
                    data-testid="button-disconnect-drive"
                  >
                    {disconnectDriveMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Disconnect"}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  className="h-7"
                  onClick={() => connectDriveMutation.mutate()}
                  disabled={connectDriveMutation.isPending}
                  data-testid="button-connect-drive"
                >
                  {connectDriveMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                  Connect
                </Button>
              )}
            </div>
          </div>
          )}
        </CardContent>
      </Card>
      )}

      {selectedCategory === "connections" && (
      /* Inbox Read Access Card — standalone so it stays visible regardless of
         send-connection state. The actual read OAuth requires a send connection
         first (enforced server-side), but we never hide the card for that. */
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Inbox read access</CardTitle>
              <CardDescription>
                Let Clarik read your inbound Gmail for reply detection, unified inbox, and AI reply features
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0" data-testid="section-gmail-read-access">
          <div className="text-sm text-muted-foreground space-y-3">
            <p className="text-foreground">
              Automatically detect replies, pause campaigns, and keep every conversation in one place.
            </p>
            <ul className="space-y-1.5">
              {[
                { title: "Reply detection", desc: "Know the moment a lead or client writes back." },
                { title: "Auto-pause drip campaigns", desc: "Stop automated follow-ups once someone replies." },
                { title: "Unified inbox", desc: "Read and respond to every thread without leaving Clarik." },
                { title: "Activity timeline", desc: "Inbound replies appear right alongside your outreach." },
                { title: "AI-drafted follow-ups", desc: "Get a ready-to-edit reply suggested for you." },
              ].map((b) => (
                <li key={b.title} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm">
                    <span className="font-medium text-foreground">{b.title}</span>
                    <span className="text-muted-foreground"> — {b.desc}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Coming soon.</span> Inbox reading uses a restricted Google
                permission (<code className="text-[11px]">gmail.readonly</code>) that requires a third-party security audit
                we're working through. It's on our roadmap, but the audit takes many months — so we can't promise a specific
                date just yet. Early access is limited while we're in this phase.
              </p>
            </div>
          </div>

          {/* Prerequisite: a working send connection is required before the read scope
              can be activated. Show the note when neither connected nor stale-authorized
              (i.e. truly never connected). If authorized-but-stale, they can still
              request access — they just need to reconnect before enabling the scope. */}
          {!gmailStatus?.userTokensConnected && !gmailStatus?.userTokensAuthorized && !gmailLoading && (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
              <p className="text-xs text-muted-foreground">
                You'll need to{" "}
                <button
                  className="underline text-primary hover:no-underline"
                  onClick={() => connectGmailMutation.mutate()}
                  disabled={connectGmailMutation.isPending || isViewingAsUser}
                  data-testid="button-connect-gmail-for-read"
                >
                  connect Gmail (send)
                </button>{" "}
                first before inbox read access can be enabled.
              </p>
            </div>
          )}

          {/* Funnel states — visible regardless of send-connection status */}
          {gmailLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : gmailStatus?.inboundReadingAvailable ? (
            <p className="flex items-center gap-1.5 text-sm text-green-600 font-medium" data-testid="text-read-active">
              <CheckCircle className="w-4 h-4" />
              Inbox reading &amp; reply detection active
            </p>
          ) : gmailStatus?.readAccessState === "granted_pending_connect" ? (
            <div className="space-y-2" data-testid="section-read-granted">
              <p className="text-sm text-blue-600 font-medium" data-testid="text-read-granted">
                Inbox read access approved — finish connecting to enable it.
              </p>
              <p className="text-xs text-muted-foreground">
                This permanently uses one of the limited read seats and cannot be undone.
                Only proceed if you need in-app inbox reading.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => connectReadMutation.mutate()}
                disabled={connectReadMutation.isPending || isViewingAsUser || !gmailStatus?.userTokensConnected}
                data-testid="button-connect-gmail-read"
              >
                {connectReadMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Enable inbox reading
              </Button>
              {!gmailStatus?.userTokensConnected && (
                <p className="text-xs text-amber-600">Connect Gmail (send) above before enabling inbox reading.</p>
              )}
            </div>
          ) : gmailStatus?.readAccessState === "requested" ? (
            <p className="text-sm text-amber-600" data-testid="text-read-requested">
              Request submitted — pending admin review. You'll be notified when a decision is made.
            </p>
          ) : gmailStatus?.readAccessState === "rejected" ? (
            <div className="space-y-2" data-testid="section-read-rejected">
              <p className="text-sm text-red-600" data-testid="text-read-rejected">
                Access request declined{gmailStatus?.readRequestDecisionNote ? `: ${gmailStatus.readRequestDecisionNote}` : "."}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => appealReadAccessMutation.mutate("")}
                disabled={appealReadAccessMutation.isPending || isViewingAsUser}
                data-testid="button-appeal-gmail-read"
              >
                {appealReadAccessMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Appeal decision
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => requestReadAccessMutation.mutate("")}
                  disabled={requestReadAccessMutation.isPending || isViewingAsUser}
                  data-testid="button-request-gmail-read"
                >
                  {requestReadAccessMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                  Request early access
                </Button>
                {readNotifyStatus?.notified ? (
                  <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium" data-testid="text-notify-confirmed">
                    <CheckCircle className="w-4 h-4" />
                    We'll let you know
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => notifyReadAccessMutation.mutate()}
                    disabled={notifyReadAccessMutation.isPending || isViewingAsUser}
                    data-testid="button-notify-gmail-read"
                  >
                    {notifyReadAccessMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    Notify me when it's live
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Early-access requests are reviewed by an admin and are subject to seat availability.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {selectedCategory === "connections" && (
      /* Task #388 — Outlook support request (demand signal only) */
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Use Outlook, Office 365, or Hotmail?</CardTitle>
              <CardDescription>
                Clarik connects Gmail today — let us know if you'd like Microsoft / Outlook support
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0" data-testid="section-outlook-request">
          <p className="text-sm text-muted-foreground">
            We currently only support connecting a Gmail / Google Workspace mailbox. If you use
            Outlook, Office 365, or Hotmail, register your interest below — we use these requests
            to decide whether to build Outlook support. This does not connect any mailbox yet.
          </p>

          {outlookRequestStatus?.requested ? (
            <p className="flex items-center gap-1.5 text-sm text-green-600 font-medium" data-testid="text-outlook-requested">
              <CheckCircle className="w-4 h-4" />
              Thanks — your request for Outlook support has been logged.
            </p>
          ) : (
            <div className="space-y-2">
              <Textarea
                placeholder="Optional: tell us how you'd use Outlook support (which provider, what you need)…"
                value={outlookReason}
                onChange={(e) => setOutlookReason(e.target.value)}
                disabled={requestOutlookSupportMutation.isPending || isViewingAsUser}
                rows={3}
                data-testid="input-outlook-reason"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => requestOutlookSupportMutation.mutate(outlookReason)}
                disabled={requestOutlookSupportMutation.isPending || isViewingAsUser}
                data-testid="button-request-outlook"
              >
                {requestOutlookSupportMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Request Outlook support
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {selectedCategory === "email" && (
      <div className="space-y-6">
      <MyEmailHealthCard />
      {/* Email Signature Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <PenLine className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Email Signature</CardTitle>
              <CardDescription>
                Customize your email signature for outgoing emails
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Signature Text Editor */}
          <div className="space-y-2">
            <Label htmlFor="emailSignature">Signature Text</Label>
            <div className="flex items-center gap-1 mb-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyFormatting('b')}
                title="Bold (select text first)"
                data-testid="button-format-bold"
              >
                <Bold className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyFormatting('i')}
                title="Italic (select text first)"
                data-testid="button-format-italic"
              >
                <Italic className="w-4 h-4" />
              </Button>
              <span className="text-xs text-muted-foreground ml-2">Select text then click to format</span>
            </div>
            <VoiceToTextTextarea
              ref={signatureTextareaRef}
              id="emailSignature"
              value={emailSignature}
              onChange={(e) => setEmailSignature(e.target.value)}
              placeholder={"Enter your email signature...\n\nExample:\nIllusionist | Speaker\n@yourusername\nmgt:: 555.123.4567\nc:: 555.987.6543\nw:: yourwebsite.com\ne:: your@email.com"}
              rows={8}
              className="font-mono text-sm"
              data-testid="textarea-email-signature"
            />
            <p className="text-xs text-muted-foreground">
              Your signature will appear at the bottom of outgoing emails. Use &lt;b&gt;bold&lt;/b&gt; and &lt;i&gt;italic&lt;/i&gt; tags for formatting.
            </p>
          </div>

          {/* Logo Toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="use-logo-switch" className="font-medium">
                  Include Company Logo
                </Label>
                <p className="text-sm text-muted-foreground">
                  Display the company logo above your signature
                </p>
              </div>
              <Switch
                id="use-logo-switch"
                checked={isLogoForced ? true : useLogoInSignature}
                onCheckedChange={(checked) => setUseLogoInSignature(checked)}
                disabled={isLogoForced}
                data-testid="switch-use-logo-signature"
              />
            </div>
            {isLogoForced && (
              <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                <Info className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Your administrator requires the company logo in all email signatures
                </p>
              </div>
            )}
            {!logoUrl && !isLogoForced && (
              <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                <Info className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  No company logo has been uploaded yet. Contact your administrator to add a logo.
                </p>
              </div>
            )}
          </div>

          {/* Signature Preview */}
          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="border rounded-lg p-4 bg-white dark:bg-zinc-900">
              <div className="space-y-3">
                {(isLogoForced || useLogoInSignature) && logoUrl && (
                  <div className="pb-2">
                    <img
                      src={logoUrl}
                      alt="Company logo"
                      className="max-h-24 max-w-[200px] object-contain"
                      data-testid="img-signature-logo-preview"
                    />
                  </div>
                )}
                {emailSignature ? (
                  <div className="whitespace-pre-wrap text-sm text-foreground" data-testid="text-signature-preview">
                    {emailSignature.split('\n').map((line, index) => {
                      // Parse lines for special formatting including bold/italic
                      const renderFormattedLine = (text: string) => {
                        // Parse bold and italic tags
                        const parts: Array<{ type: 'text' | 'bold' | 'italic' | 'link'; content: string; href?: string }> = [];
                        let remaining = text;
                        
                        while (remaining.length > 0) {
                          // Check for bold
                          const boldMatch = remaining.match(/^(.*?)<b>([\s\S]*?)<\/b>([\s\S]*)$/);
                          if (boldMatch) {
                            if (boldMatch[1]) parts.push({ type: 'text', content: boldMatch[1] });
                            parts.push({ type: 'bold', content: boldMatch[2] });
                            remaining = boldMatch[3];
                            continue;
                          }
                          
                          // Check for italic
                          const italicMatch = remaining.match(/^(.*?)<i>([\s\S]*?)<\/i>([\s\S]*)$/);
                          if (italicMatch) {
                            if (italicMatch[1]) parts.push({ type: 'text', content: italicMatch[1] });
                            parts.push({ type: 'italic', content: italicMatch[2] });
                            remaining = italicMatch[3];
                            continue;
                          }
                          
                          // Check for links
                          const linkMatch = remaining.match(/^(.*?)(https?:\/\/[^\s]+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)(.*)$/);
                          if (linkMatch) {
                            if (linkMatch[1]) parts.push({ type: 'text', content: linkMatch[1] });
                            const link = linkMatch[2];
                            const isEmail = link.includes('@') && !link.startsWith('http');
                            const href = isEmail ? `mailto:${link}` : (link.startsWith('http') ? link : `https://${link}`);
                            parts.push({ type: 'link', content: link, href });
                            remaining = linkMatch[3];
                            continue;
                          }
                          
                          // No more matches, add remaining as text
                          parts.push({ type: 'text', content: remaining });
                          break;
                        }
                        
                        return parts.map((part, i) => {
                          if (part.type === 'bold') return <strong key={i}>{part.content}</strong>;
                          if (part.type === 'italic') return <em key={i}>{part.content}</em>;
                          if (part.type === 'link') return <a key={i} href={part.href} className="text-primary underline">{part.content}</a>;
                          return <span key={i}>{part.content}</span>;
                        });
                      };
                      
                      return <div key={index}>{line ? renderFormattedLine(line) : '\u00A0'}</div>;
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    Your signature preview will appear here...
                  </p>
                )}
              </div>
            </div>
          </div>

          <Button
            onClick={handleSaveSignature}
            disabled={updateSignatureMutation.isPending}
            data-testid="button-save-signature"
          >
            {updateSignatureMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Signature
          </Button>
        </CardContent>
      </Card>
      </div>
      )}

      {selectedCategory === "dialer" && <DialerCard />}

      {selectedCategory === "goals" && (
        <>
          <GamificationPrefsCard isAdmin={effectiveRole === 'admin' || profile?.role === 'admin'} />
          <GoalsRecapEmailToggleRow />
        </>
      )}

      {selectedCategory === "leads" && (
        <>
          <LeadSettingsCard profile={profile} />
          <UserCreditBalanceCard />
          <DefaultClientTypeCard profile={profile} />
        </>
      )}

      {selectedCategory === "aiTools" && (
        <>
          <AIToolsPanel />
          <UserCreditBalanceCard />
        </>
      )}

      {selectedCategory === "accessibility" && (
        <>
          <VoiceToTextSettingsCard profile={profile} />
          <AccessibilityPrefsCard />
          <KeyboardCheatSheetCard />
        </>
      )}

      {selectedCategory === "notifications" && (
        <NotificationSettingsPage />
      )}

      {selectedCategory === "timecard" && (
        <TimecardSettingsSection />
      )}

      {selectedCategory === "calendarSharing" && (
        <>
          <Card data-testid="card-working-hours">
            <CardHeader>
              <CardTitle>Working hours</CardTitle>
              <CardDescription>
                Push notifications (and email if enabled) are held back outside your working hours. In-app notifications still land in your bell.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">Hold push notifications outside working hours</div>
                  <div className="text-xs text-muted-foreground">When off, push notifications fire at all hours.</div>
                </div>
                <Switch
                  checked={working?.enabled ?? false}
                  onCheckedChange={(v) => working && setWorking({ ...working, enabled: v })}
                  data-testid="switch-working-hours-enabled"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Time zone</Label>
                  <Select value={working?.timezone ?? ""} onValueChange={(v) => working && setWorking({ ...working, timezone: v })}>
                    <SelectTrigger data-testid="select-working-timezone"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start</Label>
                  <Input
                    type="time"
                    value={working?.start ?? ""}
                    onChange={(e) => working && setWorking({ ...working, start: e.target.value })}
                    data-testid="input-working-start"
                  />
                </div>
                <div>
                  <Label>End</Label>
                  <Input
                    type="time"
                    value={working?.end ?? ""}
                    onChange={(e) => working && setWorking({ ...working, end: e.target.value })}
                    data-testid="input-working-end"
                  />
                </div>
              </div>
              <div>
                <Label>Working days</Label>
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {DAYS.map((d, i) => {
                    const active = working?.days.includes(i) ?? false;
                    return (
                      <Button
                        key={i}
                        type="button"
                        size="sm"
                        variant={active ? "default" : "outline"}
                        onClick={() => {
                          if (!working) return;
                          const next = active
                            ? working.days.filter((x) => x !== i)
                            : [...working.days, i].sort();
                          setWorking({ ...working, days: next });
                        }}
                        data-testid={`button-working-day-${i}`}
                      >
                        {d}
                      </Button>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={() => saveWorkingHours.mutate()}
                  disabled={saveWorkingHours.isPending || !working}
                  data-testid="button-save-working-hours"
                >
                  {saveWorkingHours.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save working hours
                </Button>
              </div>
            </CardContent>
          </Card>
          <CalendarSharingSection />
        </>
      )}

      <AccessRequestsDialog open={accessRequestsOpen} onOpenChange={setAccessRequestsOpen} />
      <EventOfferingsDialog open={eventOfferingsOpen} onOpenChange={setEventOfferingsOpen} />
      <LateFeeSettingsDialog open={lateFeeSettingsOpen} onOpenChange={setLateFeeSettingsOpen} />
        </div>
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function SettingAuditCard() {
  const { data, isLoading } = useQuery<Array<{
    id: string; settingKey: string; oldValue: any; newValue: any; changedAt: string; userId: string | null;
  }>>({ queryKey: ["/api/admin/setting-audits"] });

  return (
    <Card data-testid="card-setting-audits">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Info className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Setting changes</CardTitle>
            <CardDescription>Audit trail of white-label and dialer setting edits</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !data || data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No changes logged yet.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {data.slice(0, 50).map((row) => (
              <div key={row.id} className="text-xs border rounded-md p-2" data-testid={`audit-row-${row.id}`}>
                <div className="flex justify-between">
                  <code className="font-mono">{row.settingKey}</code>
                  <span className="text-muted-foreground">{new Date(row.changedAt).toLocaleString()}</span>
                </div>
                <div className="mt-1 text-muted-foreground truncate">
                  <span className="line-through">{JSON.stringify(row.oldValue)}</span>{" → "}
                  <span>{JSON.stringify(row.newValue)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
function CalendarSharingSection() {
  const { toast } = useToast();
  const { data, isLoading, refetch } = useQuery<{
    calendarSharingMode: string;
    defaultCalendarViewUserIds: string[];
    icsToken: string | null;
  }>({ queryKey: ["/api/user/calendar-sharing"] });

  const [sharingMode, setSharingMode] = useState<string>("hidden");
  const [icsToken, setIcsToken] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!data) return;
    setSharingMode(data.calendarSharingMode ?? "hidden");
    setIcsToken(data.icsToken ?? null);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (updates: Record<string, any>) =>
      apiRequest("PATCH", "/api/user/calendar-sharing", updates),
    onSuccess: () => {
      refetch();
      toast({ title: "Calendar settings saved" });
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const handleModeChange = (val: string) => {
    setSharingMode(val);
    saveMutation.mutate({ calendarSharingMode: val });
  };

  const generateToken = async () => {
    setGenerating(true);
    try {
      const res = await apiRequest("PATCH", "/api/user/calendar-sharing", { generateIcsToken: true });
      const body = await res.json();
      setIcsToken(body.icsToken ?? null);
      toast({ title: "ICS token generated" });
    } catch {
      toast({ title: "Failed to generate token", variant: "destructive" });
    } finally { setGenerating(false); }
  };

  const revokeToken = async () => {
    try {
      await apiRequest("PATCH", "/api/user/calendar-sharing", { revokeIcsToken: true });
      setIcsToken(null);
      toast({ title: "ICS token revoked" });
    } catch {
      toast({ title: "Failed to revoke token", variant: "destructive" });
    }
  };

  const icsUrl = icsToken ? `${window.location.origin}/api/calendar/ics/${icsToken}` : null;

  if (isLoading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-6">
      <Card data-testid="card-calendar-sharing">
        <CardHeader>
          <CardTitle className="text-base">Calendar sharing mode</CardTitle>
          <p className="text-sm text-muted-foreground">
            Controls what teammates see when they add you to the Calendar Hub overlay.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={sharingMode} onValueChange={handleModeChange} data-testid="select-calendar-sharing-mode">
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="shared">Shared — show full block details (respects per-block privacy)</SelectItem>
              <SelectItem value="availability_only">Availability only — show Busy/Free, no details</SelectItem>
              <SelectItem value="hidden">Hidden — don't appear in overlays</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {sharingMode === "hidden" && "Your calendar won't show up when teammates open an overlay — you stay invisible."}
            {sharingMode === "availability_only" && "Teammates see when you're busy or free, but not what the block is called or its details."}
            {sharingMode === "shared" && "Teammates see your full block details. Blocks marked Private still show as 'Busy' only."}
          </p>
        </CardContent>
      </Card>

      <Card data-testid="card-ics-token">
        <CardHeader>
          <CardTitle className="text-base">Share your calendar as a link</CardTitle>
          <p className="text-sm text-muted-foreground">
            This makes a private link to your Clarik schedule that you can give to someone else — like an assistant or a partner — so they can follow your schedule inside their own calendar app (Google Calendar, Outlook, or Apple). It updates on its own.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Most people don't need this. If you've already connected your own calendar to Clarik, your schedule is already there — this link is just for letting someone else see it. Keep it private: anyone with the link can see your schedule.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {icsUrl ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input readOnly value={icsUrl} className="font-mono text-xs" data-testid="input-ics-url" />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { navigator.clipboard.writeText(icsUrl); toast({ title: "Copied!" }); }}
                  data-testid="button-copy-ics"
                >
                  Copy
                </Button>
              </div>
              <Button variant="destructive" size="sm" onClick={revokeToken} data-testid="button-revoke-ics">
                Turn off link
              </Button>
            </div>
          ) : (
            <Button onClick={generateToken} disabled={generating} data-testid="button-generate-ics">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create sharing link
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Wave 2 — Dialer card (browser dialer foundations)
// Task #262 — relabeled options, consent gate, workspace-lock awareness.
// ─────────────────────────────────────────────────────────────────────

const TWO_PARTY_CONSENT_STATES_LIST = [
  "California (CA)", "Connecticut (CT)", "Florida (FL)", "Illinois (IL)",
  "Maryland (MD)", "Massachusetts (MA)", "Montana (MT)", "Nevada (NV)",
  "New Hampshire (NH)", "Pennsylvania (PA)", "Washington (WA)",
];

const CONSENT_REQUIRED_MODES = ["all", "opt_out"];

interface MyEmailHealthResponse {
  last7Days: { sent: number; replies: number; bounces: number; bounceRate: number };
  mailboxConnected: boolean;
  workingHours: { enabled: boolean; active: boolean };
  verdict: "healthy" | "headsUp" | "noData";
  verdictMessage: string;
}

function MyEmailHealthCard() {
  const { data, isLoading } = useQuery<MyEmailHealthResponse>({
    queryKey: ["/api/email/my-health"],
  });

  return (
    <Card data-testid="card-my-email-health">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">My Email Health</CardTitle>
            <CardDescription>
              A quick read on how your own email sending is doing over the last 7 days
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">Email health is unavailable right now.</p>
        ) : (
          <>
            {/* Plain-English verdict */}
            <div
              className={cn(
                "flex items-start gap-2 rounded-lg border p-3",
                data.verdict === "healthy"
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : data.verdict === "headsUp"
                    ? "border-amber-500/40 bg-amber-500/5"
                    : "border-border bg-muted/40",
              )}
              data-testid="banner-email-health-verdict"
            >
              {data.verdict === "healthy" ? (
                <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
              ) : data.verdict === "headsUp" ? (
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              ) : (
                <Info className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              )}
              <p className="text-sm font-medium" data-testid="text-email-health-verdict">
                {data.verdictMessage}
              </p>
            </div>

            {/* 7-day stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <div className="text-2xl font-semibold" data-testid="text-email-health-sent">
                  {data.last7Days.sent.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">Sent</div>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <div className="text-2xl font-semibold" data-testid="text-email-health-replies">
                  {data.last7Days.replies.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">Replies</div>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <div
                  className={cn(
                    "text-2xl font-semibold",
                    data.last7Days.bounceRate >= 0.04 ? "text-amber-600" : "",
                  )}
                  data-testid="text-email-health-bounces"
                >
                  {data.last7Days.bounces.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Bounces{data.last7Days.sent > 0 ? ` (${(data.last7Days.bounceRate * 100).toFixed(1)}%)` : ""}
                </div>
              </div>
            </div>

            {/* Status indicators */}
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2" data-testid="status-email-health-mailbox">
                {data.mailboxConnected ? (
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                )}
                <span className={data.mailboxConnected ? "" : "text-muted-foreground"}>
                  {data.mailboxConnected ? "Mailbox connected" : "Mailbox not connected"}
                </span>
              </div>
              <div className="flex items-center gap-2" data-testid="status-email-health-working-hours">
                <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">
                  {!data.workingHours.enabled
                    ? "Working hours not set — sending allowed any time"
                    : data.workingHours.active
                      ? "Within working hours — sending active"
                      : "Outside working hours — sending paused"}
                </span>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              This is a personal, read-only snapshot of your own warm sending. It doesn't include
              anyone else's activity.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TimecardSettingsSection() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: settingsData, isLoading: settingsLoading } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/timecard/me/settings"],
  });

  const { data: shiftsData, isLoading: shiftsLoading } = useQuery<{ shifts: Array<{
    id: string;
    workDate: string;
    clockInAt: string;
    clockOutAt: string | null;
    clockOutSource: string | null;
    managerNote: string | null;
  }> }>({
    queryKey: ["/api/timecard/me/shifts"],
    enabled: settingsData?.enabled === true,
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => apiRequest("PATCH", "/api/timecard/me/settings", { enabled }),
    onSuccess: (_, enabled) => {
      qc.invalidateQueries({ queryKey: ["/api/timecard/me/settings"] });
      qc.invalidateQueries({ queryKey: ["/api/timecard/me/state"] });
      if (!enabled) qc.invalidateQueries({ queryKey: ["/api/timecard/me/shifts"] });
      toast({ title: enabled ? "Time Card enabled" : "Time Card disabled" });
    },
    onError: (err: any) => {
      toast({ title: "Failed to update Time Card setting", description: err?.message, variant: "destructive" });
    },
  });

  const enabled = settingsData?.enabled ?? false;

  function fmtTime(iso: string) {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch { return "—"; }
  }

  function fmtDuration(clockIn: string, clockOut: string | null) {
    if (!clockOut) return <span className="text-amber-500 dark:text-amber-400 text-xs">Open</span>;
    const ms = new Date(clockOut).getTime() - new Date(clockIn).getTime();
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  }

  const sourceLabels: Record<string, string> = {
    manual: "Manual",
    tab_close: "Tab closed",
    idle_timeout: "Idle",
    midnight: "Midnight",
    manager_edit: "Manager",
    forgot_clockout: "Auto (forgot)",
  };

  return (
    <div className="space-y-4">
      <Card data-testid="card-timecard-settings">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Time Card
          </CardTitle>
          <CardDescription>
            Track your work hours with clock-in and clock-out. When enabled, a timer appears in your dashboard header.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="timecard-settings-switch" className="text-sm font-medium">Enable Time Card</Label>
              <p className="text-xs text-muted-foreground">Show clock-in / clock-out controls in your dashboard header</p>
            </div>
            {settingsLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                id="timecard-settings-switch"
                checked={enabled}
                disabled={toggleMutation.isPending}
                onCheckedChange={(v) => toggleMutation.mutate(v)}
                data-testid="switch-timecard-settings"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {enabled && (
        <Card data-testid="card-timecard-history">
          <CardHeader>
            <CardTitle className="text-base">Your Shift History</CardTitle>
            <CardDescription>Last 30 days · up to 100 shifts</CardDescription>
          </CardHeader>
          <CardContent>
            {shiftsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : !shiftsData?.shifts?.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">No shifts recorded in the last 30 days.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Clock In</TableHead>
                      <TableHead>Clock Out</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shiftsData.shifts.map((shift) => (
                      <TableRow key={shift.id} data-testid={`row-shift-${shift.id}`}>
                        <TableCell className="font-medium text-sm">{shift.workDate}</TableCell>
                        <TableCell className="text-sm">{fmtTime(shift.clockInAt)}</TableCell>
                        <TableCell className="text-sm">{shift.clockOutAt ? fmtTime(shift.clockOutAt) : <span className="text-amber-500 dark:text-amber-400">Open</span>}</TableCell>
                        <TableCell className="text-sm">{fmtDuration(shift.clockInAt, shift.clockOutAt)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{sourceLabels[shift.clockOutSource ?? ""] ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DialerCard() {
  const { toast } = useToast();

  interface DialerSettings {
    effectiveRecordingMode: string;
    effectiveCallerId: string | null;
    recordingModeLocked: boolean;
    callerIdLocked: boolean;
    dialerEnabled: boolean;
    autoFocusLog?: boolean;
    bridgePhone?: string | null;
    routingPreference?: string;
  }

  const { data, isLoading } = useQuery<DialerSettings>({
    queryKey: ["/api/settings/dialer"],
  });

  const [recordingMode, setRecordingMode] = useState("off");
  const [autoFocusLog, setAutoFocusLog] = useState(true);
  const [dialToPhone, setDialToPhone] = useState(false);

  // Consent modal state
  const [consentOpen, setConsentOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    if (!data) return;
    setRecordingMode(data.effectiveRecordingMode || "off");
    setAutoFocusLog(data.autoFocusLog !== false);
    setDialToPhone(data.routingPreference === "phone");
  }, [data]);

  // If workspace forced a consent-required mode on mount and user hasn't
  // acknowledged yet, prompt the modal on first render.
  const [bootConsentShown, setBootConsentShown] = useState(false);
  useEffect(() => {
    if (!data || bootConsentShown) return;
    if (data.recordingModeLocked && CONSENT_REQUIRED_MODES.includes(data.effectiveRecordingMode)) {
      setBootConsentShown(true);
      setPendingMode(data.effectiveRecordingMode);
      setConsentChecked(false);
      setConsentOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleModeChange = (val: string) => {
    if (CONSENT_REQUIRED_MODES.includes(val)) {
      setPendingMode(val);
      setConsentChecked(false);
      setConsentOpen(true);
    } else {
      setRecordingMode(val);
    }
  };

  const confirmConsent = () => {
    if (!consentChecked || !pendingMode) return;
    setRecordingMode(pendingMode);
    setPendingMode(null);
    setConsentOpen(false);
  };

  const cancelConsent = () => {
    setPendingMode(null);
    setConsentOpen(false);
    setConsentChecked(false);
  };

  const recordingModeLocked = !!data?.recordingModeLocked;
  const [, navigateTo] = useLocation();

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {};
      if (!recordingModeLocked) body.recordingMode = recordingMode;
      body.autoFocusLog = autoFocusLog;
      // Per-user call routing (browser vs. phone). Server forces 'browser'
      // when no personal phone is on file. (Task #577)
      body.routingPreference = dialToPhone ? "phone" : "browser";
      const res = await apiRequest("PATCH", "/api/settings/dialer", body);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Dialer settings saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/settings/dialer"] });
    },
    onError: (e: any) => toast({ title: "Save failed", description: String(e?.message || e), variant: "destructive" }),
  });

  return (
    <>
      {/* Two-party consent acknowledgment modal */}
      <Dialog open={consentOpen} onOpenChange={(o) => { if (!o) cancelConsent(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Recording Consent Acknowledgment</DialogTitle>
            <DialogDescription>
              The recording mode you selected will automatically record calls. These U.S. states require you to inform the other party that the call is being recorded:
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-1 text-sm py-2">
            {TWO_PARTY_CONSENT_STATES_LIST.map((s) => (
              <div key={s} className="flex items-center gap-1.5 text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                {s}
              </div>
            ))}
          </div>
          <div className="flex items-start gap-3 rounded-md border p-3 mt-1">
            <Checkbox
              id="consent-check"
              checked={consentChecked}
              onCheckedChange={(v) => setConsentChecked(!!v)}
              data-testid="checkbox-recording-consent"
            />
            <label htmlFor="consent-check" className="text-sm leading-snug cursor-pointer">
              I understand that in these states I am legally required to inform the other party that the call is being recorded.
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelConsent} data-testid="button-consent-cancel">
              Cancel
            </Button>
            <Button onClick={confirmConsent} disabled={!consentChecked} data-testid="button-consent-confirm">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card data-testid="card-dialer">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <PhoneCall className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Browser Dialer</CardTitle>
              <CardDescription>
                Place calls from inside the app. Manual log entry works without Twilio installed.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <Label htmlFor="dialer-recording">Recording mode</Label>
                {recordingModeLocked ? (
                  <>
                    <div className="w-full h-9 px-3 rounded-md border border-input bg-muted text-sm flex items-center text-muted-foreground cursor-not-allowed">
                      {recordingMode === "off" && "Off — never record"}
                      {recordingMode === "all" && "Always record — every call, you can't stop it"}
                      {recordingMode === "smart" && "Smart — record unless dialing a two-party-consent state"}
                      {recordingMode === "opt_out" && "On by default — recording starts automatically, you can press Stop mid-call"}
                      {recordingMode === "opt_in" && "Off by default — you must press Record mid-call to start"}
                    </div>
                    <p className="text-xs text-muted-foreground">Controlled by your workspace admin.</p>
                  </>
                ) : (
                  <select
                    id="dialer-recording"
                    value={recordingMode}
                    onChange={(e) => handleModeChange(e.target.value)}
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="select-dialer-recording"
                  >
                    <option value="off">Off — never record</option>
                    <option value="all">Always record — every call, you can't stop it</option>
                    <option value="smart">Smart — record unless dialing a two-party-consent state (CA, CT, FL, IL, MD, MA, MT, NV, NH, PA, WA)</option>
                    <option value="opt_out">On by default — recording starts automatically, you can press Stop mid-call</option>
                    <option value="opt_in">Off by default — you must press Record mid-call to start</option>
                  </select>
                )}
                <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-2 mt-2 text-xs flex items-start gap-2">
                  <Info className="h-4 w-4 mt-0.5 text-amber-700 dark:text-amber-400 shrink-0" />
                  <span className="text-amber-900 dark:text-amber-200">
                    11 U.S. states require two-party consent before recording. Review your Knowledge Base for state-by-state rules before enabling.
                  </span>
                </div>
              </div>

              <div className="rounded-md border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Incoming call routing</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      How inbound calls are delivered to you.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <button
                    type="button"
                    onClick={() => setDialToPhone(false)}
                    className={`flex items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${!dialToPhone ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/50"}`}
                    data-testid="button-routing-browser"
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${!dialToPhone ? "border-primary" : "border-muted-foreground/40"}`}>
                      {!dialToPhone && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium">Dial from browser</div>
                      <div className="text-xs text-muted-foreground">Calls ring in your browser tab only</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!data?.bridgePhone) {
                        navigateTo("/settings?category=profile");
                        toast({ title: "Add your personal phone first", description: "Go to Profile and save a personal phone number, then come back here." });
                      } else {
                        setDialToPhone(true);
                      }
                    }}
                    className={`flex items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors ${dialToPhone ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/50"}`}
                    data-testid="button-routing-phone"
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${dialToPhone ? "border-primary" : "border-muted-foreground/40"}`}>
                      {dialToPhone && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">Dial to my phone</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {data?.bridgePhone ? `Rings your browser + ${data.bridgePhone}` : "No personal phone set — click to add one in Profile"}
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="text-sm font-medium">Auto-focus notes when a call ends</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Moves your cursor into the notes field the moment a browser call disconnects.
                  </p>
                </div>
                <Switch
                  checked={autoFocusLog}
                  onCheckedChange={setAutoFocusLog}
                  data-testid="switch-dialer-autofocus"
                />
              </div>

              <Button
                onClick={() => save.mutate()}
                disabled={save.isPending}
                data-testid="button-dialer-save"
              >
                {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}

// LEAD_COLUMN_OPTIONS lives in client/src/lib/lead-column-options.ts
// and is re-exported from the top of this file (above) for back-compat.


function LeadSettingsCard({ profile }: { profile: UserProfile | undefined }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const hideBooked = (profile as any)?.hideBookedLeads === true;
  const customized = (profile as any)?.leadColumnsCustomized === true;
  // When the user hasn't customized yet, show the workspace defaults so they
  // can see what they'd be diverging from.
  const { data: wsDefaults } = useQuery<{ defaultHiddenLeadColumns: string[] }>({
    queryKey: ["/api/workspace/lead-column-defaults"],
    staleTime: 60_000,
  });
  const effectiveHidden: string[] = customized
    ? ((profile as any)?.hiddenLeadColumns ?? [])
    : (wsDefaults?.defaultHiddenLeadColumns ?? []);
  const hiddenSet = useMemo(() => new Set(effectiveHidden), [effectiveHidden]);

  // Workspace-level direction tag setting (mirrors the toggle on the Lead Gen page).
  const { data: sandbox } = useQuery<{ settings?: { quadrantTagsEnabled?: boolean } }>({
    queryKey: ["/api/lead-gen/sandbox"],
  });
  const quadrantTagsEnabled = sandbox?.settings?.quadrantTagsEnabled !== false;

  const quadrantSettingMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiRequest("PATCH", "/api/lead-gen/settings", { quadrantTagsEnabled: enabled });
      return response.json();
    },
    onSuccess: (_data, enabled) => {
      qc.invalidateQueries({ queryKey: ["/api/lead-gen/sandbox"] });
      toast({
        title: enabled ? "Direction tags enabled" : "Direction tags disabled",
        description: enabled
          ? "New lead-gen searches will tag results by compass quadrant."
          : "New searches will no longer tag results. Existing tags are hidden and will reappear if you re-enable direction tags.",
      });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Could not update setting.", variant: "destructive" });
    },
  });

  const mutation = useMutation({
    mutationFn: async (payload: { hideBookedLeads?: boolean; hiddenLeadColumns?: string[] }) => {
      const response = await apiRequest("PATCH", "/api/profile/lead-settings", payload);
      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/profile"] });
      qc.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Saved", description: "Lead settings updated." });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Could not update setting.", variant: "destructive" });
    },
  });

  const toggleColumn = (key: string, visible: boolean) => {
    const next = new Set(hiddenSet);
    if (visible) {
      next.delete(key);
    } else {
      next.add(key);
    }
    // Server flips `leadColumnsCustomized = true` automatically when
    // `hiddenLeadColumns` is sent, so the user's choice now sticks.
    mutation.mutate({ hiddenLeadColumns: Array.from(next) });
  };

  const resetToWorkspaceMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("PATCH", "/api/profile/lead-settings", {
        resetLeadColumnsToWorkspace: true,
      });
      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/profile"] });
      qc.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({ title: "Reset", description: "Now using workspace defaults." });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Could not reset.", variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Lead Settings</CardTitle>
            <CardDescription>
              Control how booked leads appear and which columns are visible in your Leads list.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="hide-booked-toggle">Hide Booked Leads</Label>
            <p className="text-sm text-muted-foreground">
              When off, booked leads stay at the top of your list highlighted in green so you can see your wins.
              When on, they're hidden from view (they remain available as Clients).
            </p>
          </div>
          <Switch
            id="hide-booked-toggle"
            checked={hideBooked}
            disabled={mutation.isPending}
            onCheckedChange={(v) => mutation.mutate({ hideBookedLeads: v })}
            data-testid="switch-hide-booked-leads"
          />
        </div>

        <div className="border-t pt-6 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="quadrant-tags-toggle" className="flex items-center gap-2">
                <Compass className="w-4 h-4" />
                Direction Tags
              </Label>
              <p className="text-sm text-muted-foreground">
                Tag Lead Gen results with their compass quadrant (NE/NW/SE/SW) relative to the search center.
                Tags are carried onto imported leads and shown next to the account name.
              </p>
            </div>
            <Switch
              id="quadrant-tags-toggle"
              checked={quadrantTagsEnabled}
              disabled={quadrantSettingMutation.isPending}
              onCheckedChange={(v) => quadrantSettingMutation.mutate(v)}
              data-testid="switch-quadrant-tags"
            />
          </div>
        </div>

        <div className="border-t pt-6 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label>Visible Columns</Label>
              <p className="text-sm text-muted-foreground mt-1">
                Choose which fields appear as columns in your Leads spreadsheet. Account Name is always shown.
                {customized
                  ? " You've customized your own — workspace default changes won't override your picks."
                  : " You're currently using your workspace's default columns."}
              </p>
            </div>
            {customized && (
              <Button
                size="sm"
                variant="outline"
                disabled={resetToWorkspaceMutation.isPending}
                onClick={() => resetToWorkspaceMutation.mutate()}
                data-testid="button-reset-lead-columns-to-workspace"
              >
                {resetToWorkspaceMutation.isPending ? "Resetting…" : "Use workspace defaults"}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
            {LEAD_COLUMN_OPTIONS.map((col) => {
              const isVisible = !hiddenSet.has(col.key);
              return (
                <label
                  key={col.key}
                  className="flex items-center gap-2 text-sm cursor-pointer hover-elevate active-elevate-2 rounded-md p-2"
                  data-testid={`label-lead-column-${col.key}`}
                >
                  <Checkbox
                    checked={isVisible}
                    disabled={mutation.isPending}
                    onCheckedChange={(v) => toggleColumn(col.key, v === true)}
                    data-testid={`checkbox-lead-column-${col.key}`}
                  />
                  <span>{col.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DefaultClientTypeCard({ profile }: { profile: UserProfile | undefined }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const currentValue = (profile as any)?.defaultClientTypeId ?? "__workspace__";

  const { data: clientTypes = [] } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["/api/client-types"],
  });

  const mutation = useMutation({
    mutationFn: async (next: string | null) => {
      const response = await apiRequest("PATCH", "/api/profile/client-type-default", { defaultClientTypeId: next });
      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/profile"] });
      toast({ title: "Saved", description: "Default client type updated." });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Could not update setting.", variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Default Client Type</CardTitle>
            <CardDescription>
              Pre-selected when you create new leads, clients, or invoices. Set to "Workspace default" to inherit.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="default-client-type">My default client type</Label>
          <Select
            value={currentValue}
            onValueChange={(v) => mutation.mutate(v === "__workspace__" ? null : v)}
            disabled={mutation.isPending}
          >
            <SelectTrigger id="default-client-type" className="w-[240px]" data-testid="select-default-client-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__workspace__">Workspace default</SelectItem>
              {clientTypes.map((ct) => (
                <SelectItem key={ct.id} value={ct.id} data-testid={`option-default-client-type-${ct.id}`}>
                  {ct.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}

function VoiceToTextSettingsCard({ profile }: { profile: UserProfile | undefined }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const enabled = profile?.voiceToTextEnabled === true;
  const speechSupported = typeof window !== "undefined" && (
    !!(window as any).SpeechRecognition || !!(window as any).webkitSpeechRecognition
  );

  const mutation = useMutation({
    mutationFn: async (next: boolean) => {
      const response = await apiRequest("PATCH", "/api/profile/voice-to-text", { enabled: next });
      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/profile"] });
      toast({ title: "Saved", description: "Voice-to-text setting updated." });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "Could not update setting.", variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <PenLine className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Voice-to-Text Button</CardTitle>
            <CardDescription>
              Show a small floating microphone button in the corner of the screen for dictating into any text field.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="voice-to-text-toggle">Show floating microphone</Label>
            <p className="text-sm text-muted-foreground">
              {speechSupported
                ? "Click an input first, then tap the mic to start dictating. Tap again to stop."
                : "Your current browser does not support voice recognition. Try Chrome, Edge, or Safari."}
            </p>
          </div>
          <Switch
            id="voice-to-text-toggle"
            checked={enabled}
            disabled={mutation.isPending || !speechSupported}
            onCheckedChange={(v) => mutation.mutate(v)}
            data-testid="switch-voice-to-text"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function GoalsRecapEmailToggleRow() {
  const { toast } = useToast();
  const { data: prefs } = useQuery<{ endOfDayRecapEmailEnabled: boolean }>({
    queryKey: ["/api/gamification/prefs"],
  });
  const update = useMutation({
    mutationFn: async (patch: { endOfDayRecapEmailEnabled: boolean }) => {
      const res = await apiRequest("PATCH", "/api/gamification/prefs", patch);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/gamification/prefs"] }),
    onError: (e: any) =>
      toast({ title: "Couldn't update preferences", description: e?.message || "Try again", variant: "destructive" }),
  });
  return (
    <div
      className="flex items-center justify-between gap-4 rounded-lg border bg-card p-4"
      data-testid="row-goals-recap-email"
    >
      <div className="space-y-1">
        <Label htmlFor="switch-eod-recap-email" className="font-medium">
          Goals recap emails
        </Label>
        <p className="text-xs text-muted-foreground">
          Email me my Daily Drive recap at the workspace cutoff hour.
        </p>
      </div>
      <Switch
        id="switch-eod-recap-email"
        checked={!!prefs?.endOfDayRecapEmailEnabled}
        onCheckedChange={(v) => update.mutate({ endOfDayRecapEmailEnabled: v })}
        data-testid="switch-eod-recap-email"
      />
    </div>
  );
}

function GamificationPrefsCard({ isAdmin = false }: { isAdmin?: boolean }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: prefs } = useQuery<{
    widgetEnabled: boolean;
    theme: string | null;
    reduceMotion: boolean;
    silentMode: boolean;
    endOfDaySummaryEnabled: boolean;
    endOfDayRecapEmailEnabled: boolean;
    weeklyRecapEmailEnabled: boolean;
  }>({
    queryKey: ["/api/gamification/my-prefs"],
  });
  const { data: ws } = useQuery<{ enabled: boolean; defaultTheme: string; summaryCutoffHour: number }>({
    queryKey: ["/api/gamification/workspace-settings"],
  });

  const update = useMutation({
    mutationFn: async (patch: Record<string, unknown>) =>
      apiRequest("PATCH", "/api/gamification/my-prefs", patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/gamification/my-prefs"] });
      qc.invalidateQueries({ queryKey: ["/api/gamification/snapshot"] });
      toast({ title: "Preference saved" });
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });


  if (!ws?.enabled) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <PenLine className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Goals</CardTitle>
              <CardDescription>Not currently enabled in this workspace.</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card data-testid="card-gamification-prefs">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <PenLine className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Goals</CardTitle>
            <CardDescription>
              Tune the activity widget that appears at the top of your dashboard.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="g-widget-enabled">Show widget on my dashboard</Label>
            <p className="text-xs text-muted-foreground">Turn the daily-drive trackers on or off.</p>
          </div>
          <Switch
            id="g-widget-enabled"
            checked={!!prefs?.widgetEnabled}
            onCheckedChange={(v) => update.mutate({ widgetEnabled: v })}
            data-testid="switch-widget-enabled"
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="g-theme">Theme</Label>
          <select
            id="g-theme"
            className="border rounded px-2 py-1 text-sm bg-background"
            value={prefs?.theme || ""}
            onChange={(e) => update.mutate({ theme: e.target.value || null })}
            data-testid="select-my-theme"
          >
            <option value="">Inherit ({ws.defaultTheme})</option>
            <option value="equalizer">Equalizer</option>
            <option value="test_tubes">Test Tubes</option>
            <option value="marquee">Marquee</option>
          </select>
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="g-silent">Silent mode</Label>
            <p className="text-xs text-muted-foreground">Mutes all sound effects.</p>
          </div>
          <Switch
            id="g-silent"
            checked={!!prefs?.silentMode}
            onCheckedChange={(v) => update.mutate({ silentMode: v })}
            data-testid="switch-silent-mode"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="g-motion">Reduce motion</Label>
            <p className="text-xs text-muted-foreground">Minimal animation; respects accessibility settings.</p>
          </div>
          <Switch
            id="g-motion"
            checked={!!prefs?.reduceMotion}
            onCheckedChange={(v) => update.mutate({ reduceMotion: v })}
            data-testid="switch-reduce-motion"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="g-eod">End-of-day summary</Label>
            <p className="text-xs text-muted-foreground">Show me yesterday's recap when I sign in.</p>
          </div>
          <Switch
            id="g-eod"
            checked={prefs?.endOfDaySummaryEnabled !== false}
            onCheckedChange={(v) => update.mutate({ endOfDaySummaryEnabled: v })}
            data-testid="switch-eod-summary"
          />
        </div>
      </CardContent>
    </Card>
  );
}

const A11Y_STORAGE_KEY = "a11y-prefs-v1";
type A11yPrefs = { reduceMotion: boolean; largeText: boolean; highContrast: boolean };
function readA11yPrefs(): A11yPrefs {
  if (typeof window === "undefined") return { reduceMotion: false, largeText: false, highContrast: false };
  try {
    const raw = localStorage.getItem(A11Y_STORAGE_KEY);
    if (!raw) return { reduceMotion: false, largeText: false, highContrast: false };
    const v = JSON.parse(raw);
    return {
      reduceMotion: !!v?.reduceMotion,
      largeText: !!v?.largeText,
      highContrast: !!v?.highContrast,
    };
  } catch {
    return { reduceMotion: false, largeText: false, highContrast: false };
  }
}
function applyA11yPrefs(prefs: A11yPrefs) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("a11y-reduce-motion", prefs.reduceMotion);
  root.classList.toggle("a11y-large-text", prefs.largeText);
  root.classList.toggle("a11y-high-contrast", prefs.highContrast);
}

function AccessibilityPrefsCard() {
  const [prefs, setPrefs] = useState<A11yPrefs>(() => readA11yPrefs());
  useEffect(() => {
    applyA11yPrefs(prefs);
    try {
      localStorage.setItem(A11Y_STORAGE_KEY, JSON.stringify(prefs));
    } catch {}
  }, [prefs]);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Accessibility className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Display & motion</CardTitle>
            <CardDescription>Adjust the interface to suit your eyes and vestibular system.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="a11y-reduce-motion">Reduce motion</Label>
            <p className="text-xs text-muted-foreground">Turns off non-essential animations and transitions.</p>
          </div>
          <Switch
            id="a11y-reduce-motion"
            checked={prefs.reduceMotion}
            onCheckedChange={(v) => setPrefs((p) => ({ ...p, reduceMotion: v }))}
            data-testid="switch-a11y-reduce-motion"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="a11y-large-text">Larger text</Label>
            <p className="text-xs text-muted-foreground">Bumps base font size up about 12.5%.</p>
          </div>
          <Switch
            id="a11y-large-text"
            checked={prefs.largeText}
            onCheckedChange={(v) => setPrefs((p) => ({ ...p, largeText: v }))}
            data-testid="switch-a11y-large-text"
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="a11y-high-contrast">High contrast</Label>
            <p className="text-xs text-muted-foreground">Sharpens borders and text contrast for low-vision users.</p>
          </div>
          <Switch
            id="a11y-high-contrast"
            checked={prefs.highContrast}
            onCheckedChange={(v) => setPrefs((p) => ({ ...p, highContrast: v }))}
            data-testid="switch-a11y-high-contrast"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function KeyboardCheatSheetCard() {
  const shortcuts: Array<{ keys: string; description: string }> = [
    { keys: "Esc", description: "Close the open dialog, drawer, or popover" },
    { keys: "Tab / Shift+Tab", description: "Move focus to the next/previous interactive element" },
    { keys: "Enter", description: "Activate the focused button or submit a form" },
    { keys: "Space", description: "Toggle the focused switch, checkbox, or button" },
    { keys: "Arrow keys", description: "Navigate within menus, selects, tabs, and calendars" },
    { keys: "Cmd/Ctrl + F", description: "Browser find — search the visible page" },
    { keys: "Cmd/Ctrl + P", description: "Browser print — useful on routing sheets and invoices" },
  ];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Keyboard className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Keyboard shortcuts</CardTitle>
            <CardDescription>The keys we rely on across the app.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2" data-testid="list-cheat-sheet">
          {shortcuts.map((s) => (
            <div key={s.keys} className="flex items-start justify-between gap-3 text-sm border-b py-2 last:border-b-0">
              <kbd className="px-2 py-1 rounded bg-muted font-mono text-xs whitespace-nowrap">{s.keys}</kbd>
              <span className="text-muted-foreground text-right">{s.description}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

type AiPrefsData = {
  voiceProviderPreference: string | null;
  workspaceVoiceProvider: string | null;
  aiCatchMeUpEnabled: boolean;
};

type CoachSettingsData = {
  enabled: boolean;
  initiationFrequency: string;
  intensity: string;
  personaArchetype: string;
  coachName: string | null;
  extractCommitmentsFromCalls: boolean;
  workWindowStartMinute: number | null;
  workWindowEndMinute: number | null;
  workTimezone: string | null;
  moodLogEnabled: boolean;
  weeklyRetroEnabled: boolean;
  coachSchedulingMode: string;
};

function minutesToLabel(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${min.toString().padStart(2, "0")} ${period}`;
}

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => i * 30);

function AIToolsPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: aiPrefs, isLoading: loadingPrefs } = useQuery<AiPrefsData>({
    queryKey: ["/api/user/ai-preferences"],
  });
  const { data: coachData, isLoading: loadingCoach } = useQuery<CoachSettingsData>({
    queryKey: ["/api/coach/settings"],
  });

  const defaultCoach = {
    enabled: false,
    initiationFrequency: "weekly",
    intensity: "gentle",
    personaArchetype: "mentor",
    coachName: "",
    extractCommitmentsFromCalls: false,
    workWindowStartMinute: 480,
    workWindowEndMinute: 1080,
    workTimezone: "America/Chicago",
    moodLogEnabled: true,
    weeklyRetroEnabled: true,
    coachSchedulingMode: "strict",
  };
  const [coach, setCoach] = useState(defaultCoach);

  useEffect(() => {
    if (coachData) {
      setCoach({
        enabled: !!coachData.enabled,
        initiationFrequency: coachData.initiationFrequency ?? "weekly",
        intensity: coachData.intensity ?? "gentle",
        personaArchetype: coachData.personaArchetype ?? "mentor",
        coachName: coachData.coachName ?? "",
        extractCommitmentsFromCalls: !!coachData.extractCommitmentsFromCalls,
        workWindowStartMinute: coachData.workWindowStartMinute ?? 480,
        workWindowEndMinute: coachData.workWindowEndMinute ?? 1080,
        workTimezone: coachData.workTimezone ?? "America/Chicago",
        moodLogEnabled: coachData.moodLogEnabled !== false,
        weeklyRetroEnabled: coachData.weeklyRetroEnabled !== false,
        coachSchedulingMode: coachData.coachSchedulingMode ?? "strict",
      });
    }
  }, [coachData]);

  const coachMutation = useMutation({
    mutationFn: (data: Partial<typeof coach>) =>
      apiRequest("PATCH", "/api/coach/settings", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/coach/settings"] }),
    onError: () => toast({ title: "Failed to save coach settings", variant: "destructive" }),
  });

  function patchCoach(data: Partial<typeof coach>) {
    setCoach((prev) => ({ ...prev, ...data }));
    coachMutation.mutate(data);
  }

  if (loadingPrefs || loadingCoach) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">AI tools</CardTitle>
            <CardDescription>Personal AI behavior. Workspace-level controls live in the admin panel.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* ── Catch Me Up ────────────────────────────────────────────── */}
        <div>
          <h3 className="text-sm font-semibold mb-1">Catch Me Up</h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {aiPrefs?.aiCatchMeUpEnabled ? (
              <>
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                Enabled — the assistant can brief you on recent activity at the start of a session.
              </>
            ) : (
              <>
                <span className="inline-block w-2 h-2 rounded-full bg-muted-foreground/50 shrink-0" />
                Disabled by your workspace admin.
              </>
            )}
          </div>
        </div>

        <Separator />

        {/* ── Life coach ─────────────────────────────────────────────── */}
        <div>
          <div className="flex items-start justify-between gap-4 mb-1">
            <div>
              <h3 className="text-sm font-semibold">Life coach</h3>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-xs">
                Adds a personal coaching layer to the voice assistant. Use the{" "}
                <span className="font-medium text-foreground">Work / Coach</span> toggle inside the assistant panel to activate it per session.
              </p>
            </div>
            <Switch
              data-testid="switch-coach-enabled"
              checked={coach.enabled}
              onCheckedChange={(v) => patchCoach({ enabled: v })}
              disabled={coachMutation.isPending}
            />
          </div>

          {coach.enabled && (
            <div className="mt-5 space-y-4 border-l-2 border-primary/20 pl-4">

              {/* Cadence */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-sm">Check-in cadence</Label>
                  <p className="text-xs text-muted-foreground">How often the coach initiates a session unprompted.</p>
                </div>
                <Select
                  value={coach.initiationFrequency}
                  onValueChange={(v) => patchCoach({ initiationFrequency: v })}
                  disabled={coachMutation.isPending}
                >
                  <SelectTrigger className="w-48 shrink-0" data-testid="select-coach-cadence">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never — manual only</SelectItem>
                    <SelectItem value="after_each_event">After each event</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Intensity */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-sm">Intensity</Label>
                  <p className="text-xs text-muted-foreground">How much the coach pushes back.</p>
                </div>
                <Select
                  value={coach.intensity}
                  onValueChange={(v) => patchCoach({ intensity: v })}
                  disabled={coachMutation.isPending}
                >
                  <SelectTrigger className="w-48 shrink-0" data-testid="select-coach-intensity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gentle">Gentle — easy check-ins</SelectItem>
                    <SelectItem value="steady">Steady — regular nudges</SelectItem>
                    <SelectItem value="firm">Firm — holds you to it</SelectItem>
                    <SelectItem value="intense">Intense — no excuses</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Persona */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-sm">Persona</Label>
                  <p className="text-xs text-muted-foreground">The coaching style and voice.</p>
                </div>
                <Select
                  value={coach.personaArchetype}
                  onValueChange={(v) => patchCoach({ personaArchetype: v })}
                  disabled={coachMutation.isPending}
                >
                  <SelectTrigger className="w-48 shrink-0" data-testid="select-coach-persona">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mentor">Mentor</SelectItem>
                    <SelectItem value="best_friend">Best friend</SelectItem>
                    <SelectItem value="drill_sergeant">Drill sergeant</SelectItem>
                    <SelectItem value="warm_parent">Warm parent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Coach name */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-sm">Coach name</Label>
                  <p className="text-xs text-muted-foreground">What the coach calls itself. Leave blank for default.</p>
                </div>
                <Input
                  data-testid="input-coach-name"
                  className="w-48 shrink-0"
                  placeholder="e.g. Alex"
                  value={coach.coachName}
                  onChange={(e) => setCoach((prev) => ({ ...prev, coachName: e.target.value }))}
                  onBlur={() => coachMutation.mutate({ coachName: coach.coachName || undefined })}
                />
              </div>

              {/* Work window */}
              <div>
                <Label className="text-sm block mb-2">Work window</Label>
                <p className="text-xs text-muted-foreground mb-2">Hours the coach uses when suggesting blocks or scheduling check-ins.</p>
                <div className="flex items-center gap-2">
                  <Select
                    value={String(coach.workWindowStartMinute)}
                    onValueChange={(v) => patchCoach({ workWindowStartMinute: Number(v) })}
                    disabled={coachMutation.isPending}
                  >
                    <SelectTrigger className="w-32" data-testid="select-work-window-start">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-56">
                      {TIME_OPTIONS.map((m) => (
                        <SelectItem key={m} value={String(m)}>{minutesToLabel(m)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-muted-foreground text-sm">to</span>
                  <Select
                    value={String(coach.workWindowEndMinute)}
                    onValueChange={(v) => patchCoach({ workWindowEndMinute: Number(v) })}
                    disabled={coachMutation.isPending}
                  >
                    <SelectTrigger className="w-32" data-testid="select-work-window-end">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-56">
                      {TIME_OPTIONS.map((m) => (
                        <SelectItem key={m} value={String(m)}>{minutesToLabel(m)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-4 pt-1">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label className="text-sm">Mood check-in</Label>
                    <p className="text-xs text-muted-foreground">Coach asks how you're feeling at the start of each session.</p>
                  </div>
                  <Switch
                    data-testid="switch-mood-log"
                    checked={coach.moodLogEnabled}
                    onCheckedChange={(v) => patchCoach({ moodLogEnabled: v })}
                    disabled={coachMutation.isPending}
                  />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label className="text-sm">Weekly retro</Label>
                    <p className="text-xs text-muted-foreground">Auto-prompts a short weekly review of wins and misses.</p>
                  </div>
                  <Switch
                    data-testid="switch-weekly-retro"
                    checked={coach.weeklyRetroEnabled}
                    onCheckedChange={(v) => patchCoach({ weeklyRetroEnabled: v })}
                    disabled={coachMutation.isPending}
                  />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label className="text-sm">Extract commitments from calls</Label>
                    <p className="text-xs text-muted-foreground">Coach surfaces action items mentioned during voice sessions.</p>
                  </div>
                  <Switch
                    data-testid="switch-extract-commitments"
                    checked={coach.extractCommitmentsFromCalls}
                    onCheckedChange={(v) => patchCoach({ extractCommitmentsFromCalls: v })}
                    disabled={coachMutation.isPending}
                  />
                </div>
              </div>

              {/* Scheduling mode */}
              <div className="flex items-center justify-between gap-4 pt-1">
                <div>
                  <Label className="text-sm">Scheduling mode</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">How strictly the coach separates your work and personal time when suggesting blocks.</p>
                </div>
                <Select
                  value={coach.coachSchedulingMode}
                  onValueChange={(v) => patchCoach({ coachSchedulingMode: v })}
                  disabled={coachMutation.isPending}
                >
                  <SelectTrigger className="w-48 shrink-0" data-testid="select-coach-scheduling-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strict">Strict — hard boundary</SelectItem>
                    <SelectItem value="flexible">Flexible — gentle reminders</SelectItem>
                    <SelectItem value="off">Off — no enforcement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

